<template lang="pug">
.bg-grey.w-full.p-40
  BriqPanel
    template(v-slot:header)
      h2.h2 Visitors
      .actions
        BriqButton(:disabled="disableSaveVisitors" @click="trySaveVisitors" variant="secondary" data-test-id="save-visitors-button") Save
    .visitors-table-holder
      BriqTable.visitors-table
        tr
          th.no #
          th.nickname Nickname
          th.first-name(v-if="isSmartwaiverIntegration" :data-test-id="`visitors-first-name-column`") First name
          th.last-name(v-if="isSmartwaiverIntegration" :data-test-id="`visitors-last-name-column`") Last name
          th.e-mail Email
          th.phone(v-if="isSmartwaiverIntegration" :data-test-id="`visitors-phone-column`") Phone number
          th.note(v-if="isSmartwaiverIntegration" :data-test-id="`visitors-note-column`") Specifics
          th(v-for="p in orderedBookingProducts")
            .scheduled-booking-products
              div
                BriqCheckbox(
                  :value="isBookingProductCheckedForAllVisitors(p)"
                  :disabled="disableSaveVisitors" 
                  @input="e => toggleParticipationForAllVisitors(p, e)"
                  )
              div
                .resource-abbreviation(:style="`background-color: ${p.ResourceColor};`") {{ resourceAbbreviation(p)  }} 
              div
                .time(:data-test-id="`visitors-tab-product-from-time-${p.Key}`") {{ getTime(p.FromDateTime) }}
                .resource-name {{ p.ProductName }}
        tr(v-for="(ghost, index) in visitorGhosts")
          td.no {{ index + 1 }}
          td
            BriqInput(
              placeholder="Nickname"
              :disabled="disableSaveVisitors"
              v-model="ghost.Nickname"
              :data-test-id="`visitor-nickname-${index}`"
              @paste.prevent="pasteToClipboard"
              )
          td(v-if="isSmartwaiverIntegration")
            BriqInput(
              placeholder="First name"
              :disabled="disableSaveVisitors"
              v-model="ghost.Firstname"
              :data-test-id="`visitor-first-name-${index}`"
              @paste.prevent="e => pasteToClipboard(e, 'Firstname')"
              )
          td(v-if="isSmartwaiverIntegration")
            BriqInput(
              placeholder="Last name"
              :disabled="disableSaveVisitors"
              v-model="ghost.Lastname"
              :data-test-id="`visitor-last-name-${index}`"
              @paste.prevent="e => pasteToClipboard(e, 'Lastname')"
              )
          td
            BriqInput(
              placeholder="E-mail" 
              :disabled="disableSaveVisitors" 
              v-model="ghost.Email" 
              :error="emailError(index)" 
              @blur="$v.visitorGhosts[index].$touch()"
              :data-test-id="`visitor-email-${index}`"
              @paste.prevent="e => pasteToClipboard(e, 'Email')"
              )
          td(v-if="isSmartwaiverIntegration")
            BriqInput(
              placeholder="Phone"
              :disabled="disableSaveVisitors"
              v-model="ghost.Phonenumber"
              :data-test-id="`visitor-phone-number-${index}`"
              @paste.prevent="e => pasteToClipboard(e, 'Phonenumber')"
              )
          td(v-if="isSmartwaiverIntegration" :data-test-id="`visitor-note-${index}`") {{ ghost.Note ? ghost.Note : '' }}
          td(v-for="p in orderedBookingProducts")
            BriqCheckbox(
              :value="visitorParticipates(ghost, p)"
              :disabled="disableSaveVisitors"
              @input="e => setParticipation(ghost, p, e)"
              :data-test-id="`visitor-set-participation-${index}`"
              )
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { email } from 'vuelidate/lib/validators'
import { includes, forEachObjIndexed, forEach, filter, sortBy, prop } from 'ramda'
import { BookingVisitorApiModel } from '../../BookingDetailsApiModel.js'
import Permission from '@v/models/UserPermissionEnum.js'
import { formatTimeFromUtc } from '@/helpers/dateTimeFormatter.js'
import splitPastedData from '@/helpers/splitPastedData.js'
import BusinessFeature from '@v/models/BusinessFeatureEnum.js'

export default {
  name: 'VisitorsTab',
  data() {
    return { visitorGhosts: [] }
  },
  validations() {
    return {
      visitorGhosts: { ...this.visitorGhostsValidations() },
    }
  },
  computed: {
    ...mapGetters('bookingDetails', [
      'isPending',
      'visitors',
      'booking',
      'scheduledBookingProducts',
      'numberOfVisitors',
      'isSmartwaiverIntegration',
    ]),
    ...mapGetters('briqState', ['userHasPermission', 'businessUnitHasFeature']),
    disableSaveVisitors() {
      return (
        this.isPending ||
        !this.userHasPermission(Permission.API_Admin_Booking_Visitor_Update) ||
        !this.businessAllowsEditVisitors
      )
    },
    businessAllowsEditVisitors() {
      return this.businessUnitHasFeature(BusinessFeature.BookingEditVisitors)
    },
    orderedBookingProducts() {
      return sortBy(prop('FromDateTime'))(this.scheduledBookingProducts)
    },
  },
  methods: {
    ...mapActions('bookingDetails', ['saveVisitors']),
    visitorParticipates(visitor, bookingProduct) {
      return includes(bookingProduct.Key)(visitor.BookingProductKeys)
    },
    toggleParticipationForAllVisitors(bookingProduct, shouldParticipate) {
      forEach(v => {
        if (shouldParticipate) v.BookingProductKeys.push(bookingProduct.Key)
        else v.BookingProductKeys = v.BookingProductKeys.filter(k => k !== bookingProduct.Key)
      })(this.visitorGhosts)
    },
    isBookingProductCheckedForAllVisitors(bookingProduct) {
      const filteredBookingProducts = filter(s => {
        return s.BookingProductKeys.includes(bookingProduct.Key)
      }, this.visitorGhosts)
      return this.visitorGhosts.length === filteredBookingProducts.length
    },
    setParticipation(visitor, bookingProduct, shouldParticipate) {
      if (shouldParticipate) visitor.BookingProductKeys.push(bookingProduct.Key)
      else
        visitor.BookingProductKeys = visitor.BookingProductKeys.filter(
          k => k !== bookingProduct.Key,
        )
    },
    async trySaveVisitors() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$notifyWarning('Validation failed, please doublecheck all fields')
        return
      }
      const success = await this.saveVisitors(this.visitorGhosts)
      if (success) {
        this.$notifySuccess('Visitors saved')
      } else {
        this.$notifyError('Something went wrong while saving visitors')
      }
    },
    getTime(dateTimeUtc) {
      return formatTimeFromUtc(this.$locale)(this.$timezone)(dateTimeUtc)
    },
    visitorGhostsValidations() {
      const visitorValidations = {}
      forEachObjIndexed((_visitor, index) => {
        visitorValidations[index] = {
          Email: { email },
        }
      })(this.visitorGhosts)
      return visitorValidations
    },
    emailError(index) {
      return (
        (this.$v.visitorGhosts[index]?.Email.$dirty &&
          this.$v.visitorGhosts[index]?.Email.$error &&
          'Email needs to be valid email address') ||
        null
      )
    },
    resourceAbbreviation(bp) {
      return bp.ResourceAbbreviation
        ? bp.ResourceAbbreviation.toUpperCase()
        : bp.ResourceName.substring(0, 2).toUpperCase()
    },
    pasteToClipboard(event, inputName = 'Nickname') {
      const pastedData = event.clipboardData.getData('text/plain')
      const splitData = splitPastedData(pastedData, this.visitorGhosts.length)
      if (splitData.length) {
        this.visitorGhosts.map((ghost, index) => (ghost[inputName] = splitData[index]))
      }
    },
  },
  mounted() {
    this.visitorGhosts = this.visitors
    if (this.visitors.length < this.numberOfVisitors) {
      while (this.visitors.length < this.numberOfVisitors)
        this.visitors.push(new BookingVisitorApiModel())
    }
  },
  beforeMount() {
    this.$setDocumentTitle(`Booking ${this.booking.Number} visitors`)
  },
}
</script>

<style scoped lang="stylus">
.visitors-table tr th:first-child,
.visitors-table tr td:first-child
  position: absolute
  min-width: 50px
  line-height: 39px
  z-index: 30
  text-align center
  border-top 0px
  border-bottom 0px
  height 48px

.visitors-table tr th:first-child
  height 44px

.visitors-table tr th:nth-child(2), .visitors-table tr td:nth-child(2)
    padding-left 60px

.visitors-table tr:nth-of-type(even) th:first-child,
.visitors-table tr:nth-of-type(even) td:first-child
  background-color #f1f3f6

.visitors-table tr:nth-of-type(odd) th:first-child,
.visitors-table tr:nth-of-type(odd) td:first-child
  background-color #fff

.visitors-table-holder
  overflow-x auto

.visitors-table
  display block
  width 0px

  .nickname
    min-width 250px
  .first-name, .last-name, .phone
    min-width 200px
  .e-mail
    min-width 300px

  th, td
     min-width 220px

    .scheduled-booking-products
      display flex

      .time
        font-size 12px
        font-weight 800

      .resource-name
        font-size 12px
        font-weight 400
        line-height 12px

    .resource-abbreviation
      background-color gray
      width 17px
      height 17px
      margin-left 0px
      margin-top 9px
      margin-right 7px
      border-radius 3px
      font-size 9px
      color #fff
      font-weight 700
      text-align center
      line-height 17px
</style>
